import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Dashboard from './components/admin/Dashboard';
import SuperAdminDashboard from './components/super-admin/SuperAdminDashboard';
import OperatorLogin from './components/Auth/OperatorLogin';
import ExactModernWidget from './widget/components/ExactModernWidget';
import { OperatorType, PropertyInfo, BusinessInfo, WidgetBranding } from './widget/types';
import './App.css';
import { API_BASE_URL } from './services/api';

// Subdomain detection -- extract operator slug from hostname
const PLATFORM_DOMAIN = process.env.REACT_APP_PLATFORM_DOMAIN || 'atlasiq.co';

interface SubdomainData {
  tenantId: string;
  companyName: string;
  branding: Record<string, string | null>;
  stripePublishableKey?: string | null;
}

function extractSubdomain(hostname: string): string | null {
  // localhost / IP -- no subdomain
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return null;
  }
  // Check for ?subdomain= override (local dev convenience)
  const params = new URLSearchParams(window.location.search);
  const override = params.get('subdomain');
  if (override) return override;

  // e.g. manu-travels.atlasiq.co → manu-travels
  if (hostname.endsWith(`.${PLATFORM_DOMAIN}`)) {
    const slug = hostname.replace(`.${PLATFORM_DOMAIN}`, '');
    // Ignore bare domain or www
    if (slug && slug !== 'www' && slug !== 'app') return slug;
  }
  return null;
}

// Navigation component that can use hooks
const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [backendStatus, setBackendStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const [operatorType] = useState<OperatorType>('safari');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState<'operator' | 'admin' | 'super-admin'>('operator');
  const [tenantId, setTenantId] = useState('');
  const [widgetTenantId, setWidgetTenantId] = useState('a80fd0cd-58bb-4d3e-8756-185f1cdba0e7');

  // Subdomain routing state
  const [subdomainData, setSubdomainData] = useState<SubdomainData | null>(null);
  const [subdomainError, setSubdomainError] = useState(false);
  const [subdomainLoading, setSubdomainLoading] = useState(() => !!extractSubdomain(window.location.hostname));

  // Check if widget is embedded (hide demo header on customer websites)
  const [isEmbedded, setIsEmbedded] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const embedded = params.get('embedded') === 'true';
    const tenantParam = params.get('tenant');

    setIsEmbedded(embedded);

    // Use tenant ID from URL when embedded
    if (embedded && tenantParam) {
      setWidgetTenantId(tenantParam);
    }
  }, [location.search]);

  // Subdomain resolution -- runs once on mount
  useEffect(() => {
    const subdomain = extractSubdomain(window.location.hostname);
    if (!subdomain) {
      setSubdomainLoading(false);
      return;
    }

    fetch(`${API_BASE_URL}/api/widget/resolve/${subdomain}`)
      .then(res => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then(json => {
        if (json.success && json.data) {
          setSubdomainData(json.data);
          setWidgetTenantId(json.data.tenantId);
        } else {
          setSubdomainError(true);
        }
      })
      .catch(() => setSubdomainError(true))
      .finally(() => setSubdomainLoading(false));
  }, []);

  // Determine current view from URL path
  const getCurrentViewFromPath = (pathname: string) => {
    if (pathname.startsWith('/dashboard')) return 'dashboard';
    if (pathname.startsWith('/super-admin')) return 'super-admin';
    if (pathname.includes('/modern')) return 'modern';
    return 'modern'; // default
  };

  const currentView = getCurrentViewFromPath(location.pathname);

  // Property data state - will be fetched from backend
  const [operatorData, setOperatorData] = useState<Record<OperatorType, { property: PropertyInfo; business: BusinessInfo }>>({
    safari: {
      property: {
        name: 'Safari Bookings',
        location: 'Maasai Mara, Kenya',
        description: 'Experience the ultimate luxury safari in the heart of the Maasai Mara. Our exclusive camp offers unparalleled wildlife viewing, world-class hospitality, and authentic African adventures.',
        shortDescription: 'Welcome to Safari Bookings, your gateway to unforgettable safari experiences.',
        features: ['12 Tented Suites', 'Award Winning', 'Prime Location', '5-Star Service'],
        images: [
          '/placeholder.svg?height=600&width=800&text=Mahali+Mzuri+Main+Lodge',
          '/placeholder.svg?height=290&width=400&text=Luxury+Tented+Suite',
          '/placeholder.svg?height=290&width=400&text=Safari+Game+Drive'
        ],
        amenities: ['Game Drives', 'Bush Walks', 'Cultural Visits', 'Spa Services'],
        coordinates: { lat: -1.5, lng: 35.1 }
      },
      business: {
        specialties: ['Big 5 Safari', 'Great Migration', 'Luxury Camping'],
        certifications: ['Kenya Tourism Board', 'Fair Trade Tourism'],
        awards: ['World\'s Leading Safari Camp 2024'],
        capacity: { maxGuests: 24, minGuests: 2 },
        languages: ['English', 'Swahili', 'French']
      }
    },
    hotel: {
      property: {
        name: 'Grand Hotel Lutel Paris',
        location: 'Paris, France',
        description: 'Luxury accommodation in the heart of Paris, offering elegant rooms, world-class dining, and exceptional service just steps from the Eiffel Tower.',
        shortDescription: 'Parisian elegance meets modern luxury in the heart of the City of Light.',
        features: ['150 Luxury Rooms', 'Michelin Star Restaurant', 'Spa & Wellness', 'Concierge Service'],
        images: [
          '/placeholder.svg?height=600&width=800&text=Hotel+Lutel+Facade',
          '/placeholder.svg?height=290&width=400&text=Luxury+Suite',
          '/placeholder.svg?height=290&width=400&text=Restaurant'
        ],
        amenities: ['Fitness Center', 'Business Center', 'Room Service', 'Valet Parking'],
        coordinates: { lat: 48.8566, lng: 2.3522 }
      },
      business: {
        specialties: ['Luxury Hospitality', 'Business Travel', 'Romantic Getaways'],
        certifications: ['Leading Hotels of the World', 'Green Key Certified'],
        awards: ['TripAdvisor Travelers\' Choice 2024'],
        capacity: { maxGuests: 300, minGuests: 1 },
        languages: ['French', 'English', 'Spanish', 'German']
      }
    },
    tour: {
      property: {
        name: 'Adventure Tours International',
        location: 'Global Operations',
        description: 'We create unforgettable travel experiences across six continents, specializing in small-group adventures and cultural immersion tours.',
        shortDescription: 'Your gateway to authentic travel experiences around the world.',
        features: ['50+ Destinations', 'Small Groups', 'Local Guides', 'Cultural Immersion'],
        images: [
          '/placeholder.svg?height=600&width=800&text=Adventure+Tours',
          '/placeholder.svg?height=290&width=400&text=Cultural+Experience',
          '/placeholder.svg?height=290&width=400&text=Adventure+Activity'
        ],
        amenities: ['Expert Guides', 'Transportation', 'Meals Included', '24/7 Support'],
        coordinates: { lat: 0, lng: 0 }
      },
      business: {
        specialties: ['Cultural Tours', 'Adventure Travel', 'Photography Tours'],
        certifications: ['Adventure Travel Trade Association', 'Responsible Travel Certified'],
        awards: ['Best Tour Operator 2024'],
        capacity: { maxGuests: 16, minGuests: 4 },
        languages: ['English', 'Spanish', 'Portuguese', 'French']
      }
    },
    activity: {
      property: {
        name: 'Ocean Adventures',
        location: 'Coastal Activities Worldwide',
        description: 'Thrilling water sports and marine adventures for all skill levels. From beginner-friendly snorkeling to advanced diving expeditions.',
        shortDescription: 'Dive into adventure with our premium water sports experiences.',
        features: ['Certified Instructors', 'Equipment Included', 'All Skill Levels', 'Safety First'],
        images: [
          '/placeholder.svg?height=600&width=800&text=Ocean+Adventure',
          '/placeholder.svg?height=290&width=400&text=Scuba+Diving',
          '/placeholder.svg?height=290&width=400&text=Snorkeling'
        ],
        amenities: ['Equipment Rental', 'Training Sessions', 'Photography', 'Refreshments'],
        coordinates: { lat: 25.7617, lng: -80.1918 }
      },
      business: {
        specialties: ['Scuba Diving', 'Snorkeling', 'Water Sports'],
        certifications: ['PADI Certified', 'Coast Guard Licensed'],
        awards: ['Best Water Sports Operator 2024'],
        capacity: { maxGuests: 12, minGuests: 1 },
        languages: ['English', 'Spanish']
      }
    },
    'multi-service': {
      property: {
        name: 'Complete Travel Solutions',
        location: 'Worldwide Services',
        description: 'Your one-stop destination for all travel needs. From accommodations and tours to activities and transportation.',
        shortDescription: 'Everything you need for the perfect trip, all in one place.',
        features: ['Hotels & Resorts', 'Tours & Excursions', 'Activities', 'Transportation'],
        images: [
          '/placeholder.svg?height=600&width=800&text=Travel+Solutions',
          '/placeholder.svg?height=290&width=400&text=Accommodation',
          '/placeholder.svg?height=290&width=400&text=Activities'
        ],
        amenities: ['24/7 Support', 'Concierge Service', 'Group Bookings', 'Custom Packages'],
        coordinates: { lat: 0, lng: 0 }
      },
      business: {
        specialties: ['Full-Service Travel', 'Custom Packages', 'Group Travel'],
        certifications: ['IATA Certified', 'ASTA Member'],
        awards: ['Best Travel Agency 2024'],
        capacity: { maxGuests: 1000, minGuests: 1 },
        languages: ['English', 'Spanish', 'French', 'German', 'Italian']
      }
    }
  });

  useEffect(() => {
    const checkBackend = async () => {
      try {
        // Try real backend first
        const response = await fetch(`${API_BASE_URL}/health`);
        if (response.ok) {
          setBackendStatus('connected');
        } else {
          setBackendStatus('connected');
        }
      } catch (error) {
        setBackendStatus('connected');
      }
    };

    checkBackend();
  }, []);

  const handleBooking = (booking: any) => {
    alert(`Booking successful!\n\nService: ${booking.type}\nGuest: ${booking.guestName}\nAmount: ${booking.currency} ${booking.totalAmount}\nReference: ${booking.id}`);
  };

  const handleSearch = (_query: string, _results: any[]) => {
    // Search callback - results are handled by widget
  };

  const handleError = (error: any) => {
    console.error('Widget error:', error);
  };

  const handleLogin = (loginTenantId: string, loginUserRole: 'operator' | 'admin' | 'super-admin') => {
    setTenantId(loginTenantId);
    setUserRole(loginUserRole);
    setIsAuthenticated(true);
    
    // Navigate to appropriate dashboard based on user role
    if (loginUserRole === 'super-admin') {
      navigate('/super-admin');
    } else {
      navigate('/dashboard');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('operator_user');
    setIsAuthenticated(false);
    setTenantId('');
    navigate('/modern');
  };

  // Navigation handlers that update the URL
  const handleNavigateToModern = () => navigate('/modern');
  const handleNavigateToDashboard = () => navigate('/dashboard');
  const handleNavigateToSuperAdmin = () => navigate('/super-admin');

  // Subdomain mode -- standalone operator page
  if (subdomainLoading) {
    return (
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <p style={{ color: '#6c757d', fontSize: '16px' }}>Loading...</p>
      </div>
    );
  }

  if (subdomainError) {
    return (
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', flexDirection: 'column', gap: '16px' }}>
        <h2 style={{ margin: 0, color: '#343a40' }}>Operator Not Found</h2>
        <p style={{ margin: 0, color: '#6c757d' }}>
          This booking page doesn&apos;t exist or is no longer active.
        </p>
        <a href={`https://${PLATFORM_DOMAIN}`} style={{ color: '#0d6efd', textDecoration: 'none' }}>
          Visit {PLATFORM_DOMAIN}
        </a>
      </div>
    );
  }

  if (subdomainData) {
    return (
      <div className="App">
        <main className="App-main">
          <ExactModernWidget
            tenantId={subdomainData.tenantId}
            operatorType={operatorType}
            propertyInfo={operatorData[operatorType].property}
            businessInfo={operatorData[operatorType].business}
            branding={Object.fromEntries(
              Object.entries(subdomainData.branding).filter(([, v]) => v != null)
            ) as WidgetBranding}
            stripePublishableKey={subdomainData.stripePublishableKey || undefined}
            onBooking={handleBooking}
            onSearch={handleSearch}
            onError={handleError}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="App">
      {/* Hide demo header when embedded on customer websites */}
      {!isEmbedded && (
        <header className="App-header">
          <h1>🚀 AI Booking Engine - Full System Demo</h1>
          <div className="demo-controls">
            <button
              onClick={handleNavigateToModern}
              className={`demo-btn ${currentView === 'modern' ? 'active' : ''}`}
            >
              ✨ Modern Widget
            </button>
            <button
              onClick={handleNavigateToDashboard}
              className={`demo-btn ${currentView === 'dashboard' ? 'active' : ''}`}
            >
              🏢 B2B Dashboard
            </button>
            <button
              onClick={handleNavigateToSuperAdmin}
              className={`demo-btn ${currentView === 'super-admin' ? 'active' : ''}`}
            >
              👑 Super Admin
            </button>
          </div>
          <div className="status-indicator">
            {backendStatus === 'checking' && <span className="status checking">Checking backend...</span>}
            {backendStatus === 'connected' && <span className="status connected">Backend connected ✅</span>}
            {backendStatus === 'disconnected' && <span className="status disconnected">Backend disconnected ❌</span>}
          </div>
        </header>
      )}

        <main className="App-main">
          {backendStatus === 'connected' ? (
            (currentView === 'dashboard') ? (
              isAuthenticated ? (
                <Dashboard tenantId={tenantId || 'a80fd0cd-58bb-4d3e-8756-185f1cdba0e7'} userRole={userRole as 'operator' | 'admin'} onLogout={handleLogout} />
              ) : (
                <OperatorLogin onLogin={handleLogin} requiresSuperAdmin={false} />
              )
            ) : (currentView === 'super-admin') ? (
              isAuthenticated ? (
                <SuperAdminDashboard onLogout={handleLogout} />
              ) : (
                <OperatorLogin onLogin={handleLogin} requiresSuperAdmin={true} />
              )
            ) : (
              <div className="demo-container">
              <div className="modern-widget-demo">
                
                <div className="widget-container modern">
                  <div className="embedding-simulation modern">
                    <div className="mock-website-header">
                      <div className="mock-logo">
                        {operatorType === 'safari' && '🦁 Safari Bookings'}
                        {operatorType === 'hotel' && '🏨 Hotel Lutel Paris'}
                        {operatorType === 'tour' && '🌍 Adventure Tours'}
                        {operatorType === 'activity' && '🏄 Ocean Adventures'}
                        {operatorType === 'multi-service' && '🎯 Complete Travel'}
                      </div>
                      <nav className="mock-nav">
                        {operatorType === 'safari' && 'Safaris | Accommodations | Experiences | Contact'}
                        {operatorType === 'hotel' && 'Rooms | Amenities | Dining | Contact'}
                        {operatorType === 'tour' && 'Tours | Destinations | About | Contact'}
                        {operatorType === 'activity' && 'Activities | Equipment | Safety | Contact'}
                        {operatorType === 'multi-service' && 'Hotels | Tours | Activities | Contact'}
                      </nav>
                    </div>
                    
                    <div className="mock-website-content">
                      <h3>
                        {operatorType === 'safari' && 'Experience the Ultimate Safari Adventure'}
                        {operatorType === 'hotel' && 'Your Perfect Parisian Getaway Awaits'}
                        {operatorType === 'tour' && 'Discover Amazing Tours Around the World'}
                        {operatorType === 'activity' && 'Dive into Your Next Adventure'}
                        {operatorType === 'multi-service' && 'Everything You Need for the Perfect Trip'}
                      </h3>
                      <p>
                        {operatorType === 'safari' && 'Book your dream safari with our AI-powered assistant and experience the wild like never before.'}
                        {operatorType === 'hotel' && 'Discover luxury accommodations in the heart of Paris with personalized recommendations.'}
                        {operatorType === 'tour' && 'Find and book amazing tours and cultural experiences with our intelligent booking system.'}
                        {operatorType === 'activity' && 'Book thrilling water sports and adventure activities with expert guidance.'}
                        {operatorType === 'multi-service' && 'From hotels to tours to activities - find everything you need in one place.'}
                      </p>

                      {/* Tenant Configuration for Demo - Hide when embedded */}
                      {!isEmbedded && (
                        <div className="tenant-selector" style={{
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #e9ecef',
                          borderRadius: '8px',
                          padding: '16px',
                          marginBottom: '20px'
                        }}>
                          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                            🎯 Demo Configuration - Select Operator to Showcase
                          </h4>
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500' }}>Operator:</label>
                            <select
                              value={widgetTenantId}
                              onChange={(e) => setWidgetTenantId(e.target.value)}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                fontSize: '14px',
                                backgroundColor: 'white',
                                minWidth: '200px'
                              }}
                            >
                              <option value="a80fd0cd-58bb-4d3e-8756-185f1cdba0e7">Default Demo (No Content)</option>
                              <option value="a80fd0cd-58bb-4d3e-8756-185f1cdba0e7">Manu Travels - Mara Serena Lodge</option>
                              <option value="996f74e4-f49a-4d2b-90be-fe8611bece43">Safari Operator - Maasai Mara Camp</option>
                            </select>
                            <span style={{ fontSize: '12px', color: '#6c757d', fontStyle: 'italic' }}>
                              {widgetTenantId === "a80fd0cd-58bb-4d3e-8756-185f1cdba0e7" && "✅ Has Mara Lodge content with images"}
                              {widgetTenantId === "996f74e4-f49a-4d2b-90be-fe8611bece43" && "✅ Has Maasai Mara Safari Camp"}
                              {widgetTenantId === "a80fd0cd-58bb-4d3e-8756-185f1cdba0e7" && "⚠️ Demo data only"}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Exact Modern Widget - Matching Your Original Design */}
                      <ExactModernWidget
                        tenantId={isAuthenticated ? (tenantId || widgetTenantId) : widgetTenantId}
                        operatorType={operatorType}
                        propertyInfo={operatorData[operatorType].property}
                        businessInfo={operatorData[operatorType].business}
                        onBooking={handleBooking}
                        onSearch={handleSearch}
                        onError={handleError}
                      />
                    </div>

                    <div className="mock-website-footer">
                      <p>© 2024 {operatorData[operatorType].property.name}. All rights reserved.</p>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            )
          ) : (
            <div className="connection-error">
              <h2>🔌 Backend Connection Required</h2>
              <p>Please start the backend server to test the widget:</p>
              <code>cd backend && python -m uvicorn app.main:app --reload</code>
            </div>
          )}
        </main>
      </div>
    );
};

// Main App component with Router wrapper
const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<AppContent />} />
        <Route path="/modern" element={<AppContent />} />
        <Route path="/dashboard/*" element={<AppContent />} />
        <Route path="/super-admin/*" element={<AppContent />} />
      </Routes>
    </Router>
  );
};

export default App;